// @ts-check

export const PROVENANCE_KEYS = new Set(['quote', 'source', 'confidence']);

export const PRIMARY_KEYS = ['finding', 'intervention', 'note', 'name', 'issue', 'text'];

const VERDICT_KEYS = ['addressed', 'status', 'answer', 'value'];

const ACRONYMS = { hipaa: 'HIPAA', dme: 'DME', mbi: 'MBI', dob: 'DOB', pcp: 'PCP', sdoh: 'SDOH', ed: 'ED', tcm: 'TCM' };

/** @param {unknown} v */
export const isBlank = v =>
  v == null || v === '' || (Array.isArray(v) && v.length === 0) ||
  (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

/** @param {string} k */
export const humanize = k =>
  String(k).replace(/_/g, ' ').replace(/\b\w+/g, w =>
    ACRONYMS[w.toLowerCase()] || w.charAt(0).toUpperCase() + w.slice(1)
  );

/** @param {Record<string, any>} item */
export const primaryKey = item => PRIMARY_KEYS.find(k => !isBlank(item[k])) || null;

/** @param {Record<string, any>} item */
export const primaryText = item => {
  const key = primaryKey(item);
  return key ? item[key] : '';
};

/** @param {[string, any][]} entries */
const byVerdict = entries => [...entries].sort((a, b) =>
  (VERDICT_KEYS.indexOf(a[0]) === -1 ? 99 : VERDICT_KEYS.indexOf(a[0]))
  - (VERDICT_KEYS.indexOf(b[0]) === -1 ? 99 : VERDICT_KEYS.indexOf(b[0]))
);

/** @param {string} k */
const isProvenance = k => PROVENANCE_KEYS.has(k) || k.endsWith('_source') || k.endsWith('_confidence');

/** @param {Record<string, any>} v */
const innerEntries = v => Object.entries(v).filter(([ik]) => !isProvenance(ik));

/** @param {unknown} v */
const isNested = v => !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * @param {{ omit?: string[] }} section
 * @returns {(k: string) => boolean}
 */
const shownKey = section => {
  const omit = new Set(section.omit || []);
  return k => !omit.has(k) && !isProvenance(k);
};

/**
 * Label/value pairs for a key_value section, nested objects flattened verdict-first.
 * @param {Record<string, any>} data
 * @param {{ omit?: string[] }} section
 * @returns {[string, string][]}
 */
export function keyValueEntries(data, section = {}) {
  const shown = shownKey(section);
  return Object.entries(data || {})
    .filter(([k, v]) => !isBlank(v) && shown(k))
    .map(([k, v]) => {
      if (isNested(v)) {
        const inner = byVerdict(innerEntries(v).filter(([, iv]) => !isBlank(iv)));
        return /** @type {[string, string]} */ ([k, inner.map(([, iv]) => String(iv)).join(' — ')]);
      }
      return /** @type {[string, string]} */ ([k, Array.isArray(v) ? v.join(', ') : String(v)]);
    })
    .filter(([, v]) => !isBlank(v));
}

/**
 * Editable fields of a key_value section; nested objects expand to one field per inner key.
 * @param {Record<string, any>} data
 * @param {{ omit?: string[] }} section
 * @returns {{ path: string[], label: string, value: any }[]}
 */
export function keyValueFields(data, section = {}) {
  const shown = shownKey(section);
  return Object.entries(data || {})
    .filter(([k]) => shown(k))
    .flatMap(([k, v]) => {
      if (isNested(v)) {
        return byVerdict(innerEntries(v)).map(([ik, iv]) => ({
          path: [k, ik],
          label: `${humanize(k)} · ${humanize(ik)}`,
          value: iv,
        }));
      }
      return [{ path: [k], label: humanize(k), value: v }];
    });
}

/**
 * Secondary fields of a list item, shown as sub-lines under its main text.
 * @param {Record<string, any> | string} item
 * @param {{ omit?: string[] }} section
 * @returns {[string, any][]}
 */
export function listItemDetails(item, section = {}) {
  if (!item || typeof item !== 'object') return [];
  const omit = new Set(section.omit || []);
  const main = primaryText(item);
  return Object.entries(item).filter(([k, v]) =>
    !omit.has(k) && !isProvenance(k) && !isBlank(v) && v !== main && !PRIMARY_KEYS.includes(k)
  );
}

/**
 * @param {{ columns?: string[] }} section
 * @param {Record<string, any>[]} rows
 * @returns {string[]}
 */
export const tableColumns = (section, rows) =>
  section.columns || Object.keys((rows && rows[0]) || {}).filter(k => !PROVENANCE_KEYS.has(k));
